'use client';

import { Button, cx, Tooltipß } from '@otter-ai/den-components';
import { UploadCloudIcon } from '@otter-ai/den-icons';
import { useDesktop } from '@otter-ai/desktop';
import type { GetFeatureLimitNotificationsResponse } from '@otter-ai/recall-client';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSubscription } from '@/features/account-settings/shared/hooks/useSubscription';
import { currentOverlayComponent } from '@/features/overlay-manager/atoms';
import { useTutorial } from '@/features/tutorials/useTutorial';
import { featureLimitNotificationsQueryCreator } from '@/query/featureLimitQueryCreator';
import { createErrorHandler } from '@/shared/components/SectionalErrorBoundary';
import { useUsageNotification } from '@/shared/hooks/subscription/useUsageNotification';
import { useModalKey } from '@/shared/modals';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import type { Subscription } from '@/shared/types/common';
import { OtterPlanType } from '@/shared/types/plan';
import { openImportPaywallDialog } from './Components/openImportPaywallDialog';
import { UploadFilesModal } from './Components/UploadFilesModal';
import { getScreenForContext, getUploadContext } from './utils/utils';

const UPLOAD_FILES_MODAL_KEY = 'upload-file-modal';

type UploadButtonProps = {
  /** Tooltip position */
  tooltipPosition?: 'right' | 'bottom';
  /** Compact mode shows only icon */
  compactButton?: boolean;
};

/**
 * UploadButton - Triggers file import flow
 *
 * Similar to /client implementation:
 * 1. Mobile device check (app download promotion)
 * 2. Pre-validation (subscription/quota checks)
 * 3. Opens UploadFilesModal
 */
export function UploadButton(props: UploadButtonProps) {
  const { tooltipPosition = 'bottom', compactButton = false } = props;
  const { supportsDesktopRedesign } = useDesktop();

  const { showModal } = useModalKey(UPLOAD_FILES_MODAL_KEY);
  const { openLimitReachedOverlay } = useUsageNotification();
  const buttonRef = useRef(null);
  const pathname = usePathname();
  const { updateTutorial } = useTutorial();
  const currentOverlay = useAtomValue(currentOverlayComponent);
  const setCurrentOverlay = useSetAtom(currentOverlayComponent);

  // Subscription validation hooks
  const { subscription, isLoading: isLoadingSubscription } = useSubscription();
  const { data: featureLimits, isLoading: isLoadingFeatureLimits } = useQuery(
    featureLimitNotificationsQueryCreator(),
  );

  const isLoading = isLoadingSubscription || isLoadingFeatureLimits;

  // TODO(Mrinal): Get user profile to check Dropbox status
  // const { data: user } = useQuery(userProfileQueryCreator());
  // const hasDropbox = user?.user?.synced_accounts?.some(
  //   (account) => account.source === 'DropBox',
  // );

  // TODO: Implement app download promotion for mobile
  // const appBlockTakeover = useAppBlockTakeover();

  // For now: Simple implementation without Dropbox menu
  const hasDropbox = false;
  const hasDropboxSync = false;

  const handleClick = async () => {
    // Don't proceed if data is still loading or missing
    if (!subscription || !featureLimits) return;

    // Dismiss the import tutorial if it's active — user demonstrated they know how to use this
    if (currentOverlay?.key === 'import') {
      updateTutorial('import', { completed: true, canPrompt: false });
      setCurrentOverlay(null);
    }

    // Trigger pre-validation flow
    await triggerImportFlow({
      pathname,
      subscription,
      featureLimits,
      showModal,
      openLimitReachedOverlay,
    });
  };

  // TODO(Mrinal): Implement Dropbox menu when user has Dropbox connected
  if (hasDropbox && hasDropboxSync) {
    // Will implement menu with "Browse" and "Dropbox" options
    // For now, fall through to simple button
  }

  return (
    <>
      <Tooltipß
        caption="Import"
        side={tooltipPosition}
        disabled={!compactButton}
      >
        <div className={cx('h-8', compactButton ? 'w-8' : 'w-fit')}>
          <Button
            ref={buttonRef}
            variant="softNeutral"
            className={cx(
              '[-webkit-app-region:no-drag]',
              compactButton ? 'aspect-square p-0' : 'px-2',
              supportsDesktopRedesign
                ? '!bg-default border-none shadow-sm hover:bg-default-hover'
                : 'rounded-lg',
            )}
            onClick={handleClick}
            data-testid="upload-button"
            data-tutorial="import-recording-button"
            iconStart={!compactButton && <UploadCloudIcon size={24} />}
            disabled={isLoading}
          >
            {compactButton ? (
              <UploadCloudIcon size={24} className="shrink-0" />
            ) : (
              <span className="text-sm font-semibold">Import</span>
            )}
          </Button>
        </div>
      </Tooltipß>
      <ErrorBoundary
        fallback={null}
        onError={createErrorHandler({
          loggerName: 'import',
          eventName: 'UploadFilesModalError',
          section: 'UploadFilesModal',
        })}
      >
        <UploadFilesModal />
      </ErrorBoundary>
    </>
  );
}

/**
 * Trigger import flow with subscription validation.
 */
export async function triggerImportFlow({
  pathname,
  subscription,
  featureLimits,
  showModal,
  openLimitReachedOverlay,
}: {
  pathname: string;
  subscription: Subscription;
  featureLimits: GetFeatureLimitNotificationsResponse;
  showModal: (key?: string | null, data?: object) => void;
  openLimitReachedOverlay: () => void;
}) {
  const isOutOfMinutes = (subscription?.seconds_left ?? 0) <= 0;

  if (isOutOfMinutes) {
    openLimitReachedOverlay();
    return;
  }

  const exceededImport = featureLimits?.feature_limit_notifications?.some(
    (notification) =>
      notification.type === 'monthly_import' &&
      notification.usage >= notification.limit,
  );

  const showImportPaywallModal =
    exceededImport && subscription?.plan_type === OtterPlanType.FREE;

  // Get context from pathname
  const context = getUploadContext(pathname);

  const screenForContext = getScreenForContext(context, pathname);
  if (showImportPaywallModal) {
    openImportPaywallDialog({ screen: screenForContext });
    return;
  }

  // Branch by context
  if (context.type === 'group') {
    const confirmed = await openConfirmDialog({
      title: 'Import to channel?',
      message: 'This will make the file visible to all channel members',
      confirmText: 'OK',
      cancelText: 'Cancel',
      isDismissible: true,
    });

    if (!confirmed) return;
  }

  showModal(UPLOAD_FILES_MODAL_KEY, { ...context });
}
